import { Component, OnInit, OnDestroy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule, ActivatedRoute, RouterLink } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';

import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import { MatTableModule } from '@angular/material/table';
import { MatMenuModule } from '@angular/material/menu';

import { MatCheckboxModule } from '@angular/material/checkbox';

import { IssueApiService } from './issue.service';
import { IssueDrawerService } from '../../shared/issue-drawer/issue-drawer.service';
import { SkeletonComponent } from '../../shared/skeleton/skeleton.component';
import { PmAdvancedApiService } from '../pm-advanced/pm-advanced.service';
import { SavedIssueFilterDto, IssueSearchHit } from '../pm-advanced/pm-advanced.models';
import { ApiService } from '../../core/services/api.service';
import {
  deadlineBadgeClass,
  deadlineDisplayLabel
} from '../shared/deadline.utils';
import {
  IssueDto,
  ISSUE_TYPE_LABELS,
  ISSUE_STATUS_LABELS,
  ISSUE_PRIORITY_LABELS,
  DEADLINE_FILTER_OPTIONS,
  ISSUE_SORT_OPTIONS,
  IssueListParams
} from './issue.models';

interface ProjectOption { id: string; name: string; code?: string; }

@Component({
  selector: 'app-issues',
  standalone: true,
  imports: [
    CommonModule, FormsModule, RouterModule, RouterLink,
    MatCardModule, MatFormFieldModule, MatInputModule, MatSelectModule,
    MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatPaginatorModule,
    MatTooltipModule, MatChipsModule, MatTableModule, MatMenuModule, MatCheckboxModule,
    SkeletonComponent
  ],
  templateUrl: './issues.component.html',
  styleUrl: './issues.component.scss'
})
export class IssuesComponent implements OnInit, OnDestroy {
  private readonly issuesApi = inject(IssueApiService);
  private readonly pmAdvanced = inject(PmAdvancedApiService);
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly issueDrawer = inject(IssueDrawerService);
  private readonly destroy$ = new Subject<void>();
  private readonly searchSubject = new Subject<string>();

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly issues = signal<IssueDto[]>([]);
  readonly totalCount = signal(0);
  readonly projects = signal<ProjectOption[]>([]);
  readonly savedFilters = signal<SavedIssueFilterDto[]>([]);
  readonly creating = signal(false);
  readonly selectedIds = signal<Set<string>>(new Set());
  readonly employees = signal<{ id: string; name: string }[]>([]);
  bulkStatus: number | '' = '';
  bulkPriority: number | '' = '';
  bulkAssigneeId = '';

  searchQuery = '';
  jqlQuery = '';
  savedFilterId = '';
  projectFilter = '';
  typeFilter: number | '' = '';
  statusFilter: number | '' = '';
  priorityFilter: number | '' = '';
  deadlineFilter = '';
  sortBy = '';
  page = 0;
  pageSize = 20;

  // Quick create
  showCreate = false;
  newSummary = '';
  newProjectId = '';
  newType = 0;
  newPriority = 2;
  newDueDate = '';

  readonly typeOptions = Object.entries(ISSUE_TYPE_LABELS).map(([v, l]) => ({ value: Number(v), label: l }));
  readonly statusOptions = Object.entries(ISSUE_STATUS_LABELS).map(([v, l]) => ({ value: Number(v), label: l }));
  readonly priorityOptions = Object.entries(ISSUE_PRIORITY_LABELS).map(([v, l]) => ({ value: Number(v), label: l }));
  readonly deadlineOptions = DEADLINE_FILTER_OPTIONS;
  readonly sortOptions = ISSUE_SORT_OPTIONS;
  readonly columns = ['select', 'key', 'summary', 'type', 'status', 'priority', 'deadline', 'remaining', 'delay', 'progress', 'assignee', 'actions'];

  ngOnInit(): void {
    const qp = this.route.snapshot.queryParamMap;
    this.projectFilter = qp.get('projectId') ?? '';

    this.searchSubject.pipe(debounceTime(350), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => { this.page = 0; this.load(); });

    this.loadProjects();
    this.loadEmployees();
    this.loadSavedFilters();
    this.load();
  }

  loadEmployees(): void {
    this.api.getPaged<{ id: string; firstName?: string; lastName?: string; fullName?: string }>('v1/employees', { page: 1, pageSize: 200 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (page) => this.employees.set((page.items ?? []).map((e: any) => ({
          id: e.id,
          name: e.fullName || `${e.firstName ?? ''} ${e.lastName ?? ''}`.trim() || e.id
        }))),
        error: () => this.employees.set([])
      });
  }

  loadSavedFilters(): void {
    const projectId = this.projectFilter || undefined;
    this.pmAdvanced.listFilters(projectId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (items) => this.savedFilters.set(Array.isArray(items) ? items : []),
      error: () => this.savedFilters.set([])
    });
  }

  onSavedFilterChange(): void {
    const filter = this.savedFilters().find(f => f.id === this.savedFilterId);
    if (filter) {
      this.jqlQuery = filter.jql;
    }
    this.page = 0;
    this.load();
  }

  runJqlSearch(): void {
    if (!this.jqlQuery.trim()) return;
    this.loading.set(true);
    this.pmAdvanced.search({
      jql: this.jqlQuery.trim(),
      projectId: this.projectFilter || undefined,
      savedFilterId: this.savedFilterId || undefined,
      skip: this.page * this.pageSize,
      take: this.pageSize
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        const hits = (result.items ?? []) as IssueSearchHit[];
        this.issues.set(hits.map(hit => ({
          id: hit.id,
          projectId: this.projectFilter || '',
          projectName: hit.projectName,
          key: hit.key,
          number: 0,
          summary: hit.summary,
          type: 0,
          typeLabel: hit.typeLabel,
          status: 0,
          statusLabel: hit.statusLabel,
          priority: 0,
          priorityLabel: hit.priorityLabel,
          assigneeName: hit.assigneeName,
          labels: [],
          components: [],
          commentCount: 0,
          attachmentCount: 0,
          subtaskCount: 0,
          createdAt: ''
        } as IssueDto)));
        this.totalCount.set(result.totalCount ?? 0);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.message || 'JQL search failed.');
        this.loading.set(false);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadProjects(): void {
    this.api.getPaged<ProjectOption>('v1/projects', { page: 1, pageSize: 100 }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (page) => {
        const items = (page.items ?? []).map((p: any) => ({
          id: String(p.id ?? p.Id),
          name: p.name ?? p.Name,
          code: p.code ?? p.Code
        }));
        this.projects.set(items);
        if (!this.newProjectId && items.length) this.newProjectId = items[0].id;
        if (!this.projectFilter && items.length === 1) this.projectFilter = items[0].id;
      },
      error: () => this.projects.set([])
    });
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    const params: IssueListParams = {
      page: this.page + 1,
      pageSize: this.pageSize,
      search: this.searchQuery || undefined,
      projectId: this.projectFilter || undefined,
      type: this.typeFilter !== '' ? this.typeFilter : undefined,
      status: this.statusFilter !== '' ? this.statusFilter : undefined,
      priority: this.priorityFilter !== '' ? this.priorityFilter : undefined,
      deadlineFilter: this.deadlineFilter || undefined,
      sortBy: this.sortBy || undefined
    };
    this.issuesApi.list(params).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        this.issues.set(result.items ?? []);
        this.totalCount.set(result.totalCount ?? 0);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to load issues.');
        this.loading.set(false);
      }
    });
  }

  onSearch(value: string): void {
    this.searchQuery = value;
    this.searchSubject.next(value);
  }

  onFilterChange(): void {
    this.page = 0;
    this.loadSavedFilters();
    if (this.jqlQuery.trim() || this.savedFilterId) {
      this.runJqlSearch();
      return;
    }
    this.load();
  }

  onPageChange(event: PageEvent): void {
    this.page = event.pageIndex;
    this.pageSize = event.pageSize;
    if (this.jqlQuery.trim() || this.savedFilterId) {
      this.runJqlSearch();
      return;
    }
    this.load();
  }

  openIssue(issue: IssueDto, event?: MouseEvent): void {
    if (event?.button === 1 || event?.ctrlKey || event?.metaKey) {
      return;
    }
    event?.preventDefault();
    this.issueDrawer.openById(issue.id);
  }

  openIssueFull(issue: IssueDto, event?: Event): void {
    event?.stopPropagation();
    this.router.navigate(['/issues', issue.id]);
  }

  previewIssue(issue: IssueDto, event: Event): void {
    event.stopPropagation();
    this.issueDrawer.openById(issue.id);
  }

  isSelected(id: string): boolean {
    return this.selectedIds().has(id);
  }

  toggleSelect(id: string, event?: Event): void {
    event?.stopPropagation();
    this.selectedIds.update(set => {
      const next = new Set(set);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  toggleSelectAll(): void {
    const all = this.issues();
    const selected = this.selectedIds();
    if (all.length && all.every(i => selected.has(i.id))) {
      this.selectedIds.set(new Set());
    } else {
      this.selectedIds.set(new Set(all.map(i => i.id)));
    }
  }

  allSelected(): boolean {
    const all = this.issues();
    return all.length > 0 && all.every(i => this.selectedIds().has(i.id));
  }

  someSelected(): boolean {
    const selected = this.selectedIds().size;
    return selected > 0 && !this.allSelected();
  }

  applyBulk(action: 'status' | 'priority' | 'delete' | 'archive' | 'assignee'): void {
    const ids = [...this.selectedIds()];
    if (!ids.length) return;
    if (action === 'delete' && !confirm(`Delete ${ids.length} issue(s)?`)) return;

    const body: {
      issueIds: string[];
      status?: number;
      priority?: number;
      assigneeId?: string;
      unassign?: boolean;
      delete?: boolean;
      archive?: boolean;
    } = { issueIds: ids };

    if (action === 'status') {
      if (this.bulkStatus === '') return;
      body.status = this.bulkStatus;
    } else if (action === 'priority') {
      if (this.bulkPriority === '') return;
      body.priority = this.bulkPriority;
    } else if (action === 'assignee') {
      if (!this.bulkAssigneeId) return;
      if (this.bulkAssigneeId === '__unassign__') body.unassign = true;
      else body.assigneeId = this.bulkAssigneeId;
    } else if (action === 'delete') {
      body.delete = true;
    } else if (action === 'archive') {
      body.archive = true;
    }

    this.issuesApi.bulkUpdate(body).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.selectedIds.set(new Set());
        this.load();
      },
      error: (err) => this.error.set(err?.message || 'Bulk update failed.')
    });
  }

  createIssue(): void {
    if (!this.newSummary.trim() || !this.newProjectId) {
      this.error.set('Summary and project are required.');
      return;
    }
    if (!this.newDueDate) {
      this.error.set('Deadline is required.');
      return;
    }
    this.creating.set(true);
    this.issuesApi.create({
      projectId: this.newProjectId,
      summary: this.newSummary.trim(),
      type: this.newType,
      priority: this.newPriority,
      dueDate: this.newDueDate
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (issue) => {
        this.creating.set(false);
        this.showCreate = false;
        this.newSummary = '';
        this.newDueDate = '';
        this.router.navigate(['/issues', issue.id]);
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to create issue.');
        this.creating.set(false);
      }
    });
  }

  deleteIssue(issue: IssueDto, event: Event): void {
    event.stopPropagation();
    if (!confirm(`Delete ${issue.key}?`)) return;
    this.issuesApi.delete(issue.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.load(),
      error: (err) => this.error.set(err?.message || 'Failed to delete issue.')
    });
  }

  priorityClass(priority: number): string {
    const map: Record<number, string> = {
      0: 'tt-badge--priority-lowest',
      1: 'tt-badge--priority-low',
      2: 'tt-badge--priority-medium',
      3: 'tt-badge--priority-high',
      4: 'tt-badge--priority-highest'
    };
    return map[priority] ?? 'tt-badge--priority-medium';
  }

  statusBadgeClass(status: number): string {
    const map: Record<number, string> = {
      0: 'tt-badge--status-open',
      1: 'tt-badge--status-progress',
      2: 'tt-badge--status-review',
      3: 'tt-badge--status-done',
      5: 'tt-badge--status-blocked'
    };
    return map[status] ?? 'tt-badge--status-neutral';
  }

  badgeClass(badge?: string, isOverdue?: boolean, completedOnTime?: boolean | null): string {
    return deadlineBadgeClass(badge, isOverdue, completedOnTime);
  }

  issueDeadlineLabel(issue: IssueDto): string {
    return deadlineDisplayLabel(issue);
  }
}
